import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { AtlasPillars } from "@/components/atlas/AtlasPillars";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { JURISDICTIONS, ATLAS_BASE_PATH } from "@/data/jurisdictions";

export default function JurisdictionDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const jurisdiction = JURISDICTIONS.find((j) => j.slug === slug);

  useDocumentMeta({
    title: jurisdiction
      ? `Company incorporation in ${jurisdiction.name} | AMADI Legal`
      : "Jurisdiction Atlas | AMADI Legal",
    rawTitle: true,
    description: jurisdiction?.line,
    path: `${ATLAS_BASE_PATH}/${slug}`,
  });

  if (!jurisdiction) {
    return (
      <div
        data-testid="jurisdiction-not-found"
        className="min-h-screen bg-canvas flex flex-col items-center justify-center px-6 text-center"
      >
        <h1 className="font-serif text-3xl text-charcoal">Jurisdiction not found.</h1>
        <button
          onClick={() => navigate(ATLAS_BASE_PATH)}
          data-testid="jurisdiction-not-found-back"
          className="mt-6 font-sans text-xs tracking-[0.14em] uppercase font-bold px-7 py-4 bg-forest text-canvas hover:bg-charcoal transition-colors"
        >
          Back to the atlas
        </button>
      </div>
    );
  }

  return (
    <div data-testid="jurisdiction-detail-page" data-jurisdiction={jurisdiction.slug}>
      <PageHero
        eyebrow={jurisdiction.sub ? `${jurisdiction.corridor} — Free zone` : jurisdiction.corridor}
        title={`Company incorporation in ${jurisdiction.name}.`}
        subtitle={jurisdiction.line}
        current={jurisdiction.name}
      />

      {/* The four pillars, repeated from the atlas page */}
      <AtlasPillars />

      {/* Key facts — rendered only when the entry carries a `facts` array */}
      {Array.isArray(jurisdiction.facts) && jurisdiction.facts.length > 0 && (
        <section data-testid="jurisdiction-key-facts" className="bg-canvas border-t border-charcoal/10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 md:py-20">
            <h2 className="font-serif text-3xl text-charcoal">Key facts</h2>
            <dl className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
              {jurisdiction.facts.map((f, i) => (
                <div key={i} data-testid={`jurisdiction-fact-${i}`}>
                  <dt className="font-sans text-[10px] tracking-[0.18em] uppercase text-charcoal/45">{f.label}</dt>
                  <dd className="mt-2 font-serif text-lg text-charcoal">{f.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {/* CTA */}
      <section data-testid="jurisdiction-cta" className="bg-forest">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-serif text-3xl md:text-4xl text-white">
            Talk to us about {jurisdiction.name}.
          </h2>
          <Link
            to={`/contact?jurisdiction=${jurisdiction.slug}`}
            data-testid="jurisdiction-cta-discuss"
            className="group shrink-0 inline-flex items-center justify-center gap-2 font-sans text-xs tracking-[0.14em] uppercase font-bold px-8 py-4 bg-[#B85C38] text-white border border-[#B85C38] transition-colors duration-300 hover:bg-[#9c4a2c] hover:border-[#9c4a2c]"
          >
            Discuss {jurisdiction.name}
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
          </Link>
        </div>
      </section>
    </div>
  );
}
